import { useState, useCallback, useEffect, RefObject } from 'react';

interface CursorFollowerPosition {
  x: number;
  y: number;
}

interface UseCursorFollowerProps {
  rootRef: RefObject<HTMLDivElement>;
  enabled: boolean;
}

interface UseCursorFollowerReturn {
  showCursorFollower: boolean;
  setShowCursorFollower: (show: boolean) => void;
  externalCursorPos: CursorFollowerPosition | null;
  setExternalCursorPos: (pos: CursorFollowerPosition | null) => void;
  calculateCursorFollowerPosition: () => CursorFollowerPosition;
}

export const useCursorFollower = ({
  rootRef,
  enabled,
}: UseCursorFollowerProps): UseCursorFollowerReturn => {
  const [showCursorFollower, setShowCursorFollower] = useState(false);
  const [externalCursorPos, setExternalCursorPos] = useState<CursorFollowerPosition | null>(null);

  const calculateCursorFollowerPosition = useCallback(() => {
    let width = rootRef.current?.clientWidth || window.innerWidth;
    let height = window.innerHeight;

    let posX = 0, posY = 0;
    posX = width - 48 - 8;

    // Desktop positioning (viewport-relative, no scroll needed)
    if (width >= 1024) {
      // console.log('desktop positioning');
      posY = height - (48 + 100);
    }

    // Mobile positioning (element-relative, needs scroll offset)
    else {
      // console.log('mobile positioning');

      // Query button row by ID for accurate mobile positioning
      const buttonRow = document.getElementById('game-over-button-row');
      if (buttonRow) {
        const rect = buttonRow.getBoundingClientRect();
        // console.log('Using button row position:', rect.bottom);
        posY = window.scrollY + rect.bottom;
      } else {
        // Fallback
        posY = window.scrollY + height - 32;
      }
    }

    return { x: posX, y: posY };
  }, [rootRef]);

  // Window resize - update CursorFollower position if visible
  useEffect(() => {
    if (!showCursorFollower || !enabled) return;
    const handleResize = () => {
      const pos = calculateCursorFollowerPosition();
      if (pos) {
        setExternalCursorPos(pos);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showCursorFollower, enabled, calculateCursorFollowerPosition]);

  // Return values
  return {
    showCursorFollower,
    setShowCursorFollower,
    externalCursorPos,
    setExternalCursorPos,
    calculateCursorFollowerPosition,
  };

};
